import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useAssignUserManagement from "../../api/hooks/useAssignUserManagement";
import useColors from "../../api/hooks/useColors";
import { VIEWER_ACCESS_DENIED_MESSAGE } from "../../utils/constants";
import CustomTooltip from "../CustomTooltip";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import LoadingIcon from "../LoadingIcon";
import AssigneeLabel from "./AssigneeLabel";
import AssigneeMenu from "./AssigneeMenu";

const AssigneeSelector = ({
  value = null,
  onChange,
  size = "small",
  hideLabel = false,
  loading = false,
  hasPermission = true,
}) => {
  // Use the custom hook to get platform users
  const { platformUsers, usersLoading } = useAssignUserManagement();
  const { trackEvent } = useAPI();

  const colors = useColors();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const buttonRef = useRef(null);
  const [assignee, setAssignee] = useState(value);

  // Update assignee when value prop changes
  useEffect(() => {
    setAssignee(value);
  }, [value]);

  const handleToggleMenu = (e) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
    trackEvent("User opened assignee menu");
  };

  const handleClose = () => {
    setIsMenuOpen(false);
  };

  const handleAssigneeChange = (newAssignee) => {
    setAssignee(newAssignee?.email ? newAssignee : null);
    if (onChange) {
      onChange(newAssignee?.email ? newAssignee.email : null);
    }
  };
  // Calculate button styles based on props
  const getButtonStyles = () => {
    const baseStyles = {
      textTransform: "none",
      paddingLeft: 1,
      paddingRight: 1,
      width: hideLabel ? "32px" : "auto",
      border: `1px solid ${colors.grey_300}`,
      height: size === "small" ? 24 : 32,
      borderRadius: hideLabel ? "8px" : "14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      gap: 1,
      "&:hover": {
        backgroundColor: colors.grey_8percent,
        border: `1px solid ${colors.gray_main}`,
      },
    };

    return {
      ...baseStyles,
      backgroundColor: colors.grey_50,
      color: colors.text_primary,
      "&:hover": {
        backgroundColor: colors.grey_100,
        border: `1px solid ${colors.gray_main}`,
      },
    };
  };

  return (
    <Box>
      <CustomToolTipWithPermission
        title={hasPermission ? "" : VIEWER_ACCESS_DENIED_MESSAGE}
        permission={hasPermission}
        placement="top"
      >
        <Box>
          <CustomTooltip
            title={
              usersLoading || !hasPermission
                ? ""
                : assignee?.display_name
                ? "Assigned to " + assignee?.display_name
                : "Assign to"
            }
            placement="top"
          >
            <Box
              ref={buttonRef}
              onClick={hasPermission ? handleToggleMenu : null}
              sx={getButtonStyles()}
            >
              {usersLoading || loading ? (
                <LoadingIcon size={12} />
              ) : (
                <AssigneeLabel assignee={assignee} hideLabel={hideLabel} />
              )}
            </Box>
          </CustomTooltip>
        </Box>
      </CustomToolTipWithPermission>

      {isMenuOpen && (
        <AssigneeMenu
          anchorEl={buttonRef.current}
          onClose={handleClose}
          initialAssignee={assignee}
          onAssigneeChange={handleAssigneeChange}
          platformUsers={platformUsers}
        />
      )}
    </Box>
  );
};

export default AssigneeSelector;
